/**
 * Das Steuerpult — eine Bibliothek für alle Abspiel- und Schalt-Knöpfe.
 * (07.09.2026)
 *
 * ## Woher das kommt
 *
 * Martin, mit zwei Bildern: *„Das sieht nicht aus wie ein Steuerpult,
 * sondern einfach nur wie drei Felder, die erkennen schon echt komisch aus.
 * Kann man bitte die ganzen Player- und Abspiel- und Aktivierungs-Buttons
 * vereinheitlichen, dass es aus einer Bibliothek kommt?"*
 *
 * Er hat recht, und es war schlimmer als es aussah: Dieselben drei Knöpfe
 * wurden an drei Stellen einzeln gebaut — im Zuhause (`zuhause-view.ts`),
 * im großen Player (`ton-leiste.ts`) und in der Abspielleiste
 * (`audio-player.ts`). Drei Namen, drei Größen, drei Vorstellungen davon,
 * was ein „an"-Zustand ist.
 *
 * ## Was ein Pult ausmacht
 *
 *   1. **Ein Gerät, nicht drei Kacheln.** Ein Rahmen außen, Haarlinien
 *      innen. Ein Kachelfeld entsteht, sobald jeder Schalter seinen eigenen
 *      Rahmen bekommt — genau das sah Martin.
 *   2. **Zeichen über Wort.** Ein Dreieck erkennt man, bevor man liest.
 *      Und ein Wort in einer Zeile bricht nicht um („Durchgehend aus"
 *      stand auf zwei Zeilen und las sich wie ein Fließtext).
 *   3. **Nichts verschwindet.** Was gerade nicht geht, wird stumpf. Ein
 *      Pult, dem Schalter fehlen, springt bei jedem Zustandswechsel unter
 *      dem Finger weg.
 *
 * ⚠️ Diese Datei baut NUR Knöpfe. Sie startet nichts und hält nichts an —
 *    was ein Schalter tut, gibt der Aufrufer mit. Wer hier anfängt, ein
 *    `<audio>` anzufassen, hat den nächsten Sonderweg erfunden.
 */

/** Die Zeichen des Pults. Ein Satz, überall derselbe. */
export const PULT_ZEICHEN = {
  abspielen:
    '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>',
  anhalten:
    '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="currentColor"><path d="M6 19h4V5H6zm8-14v14h4V5z"/></svg>',
  beenden:
    '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>',
  /** Aufnehmen ist der GEFUELLTE Kreis — das Zeichen kennt jeder. */
  aufnehmen:
    '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="currentColor"><circle cx="12" cy="12" r="6"/></svg>',
  /*
   * ⚠️ Ein Umschalter ist KEIN Punkt. Im ersten Anlauf war er ein Kreis —
   *    und stand damit neben dem Aufnahme-Kreis, der auch einer ist. Zwei
   *    gleiche Zeichen fuer zwei verschiedene Dinge; im Bild nicht
   *    auseinanderzuhalten. Ein Kippschalter sagt von selbst, was er ist.
   */
  kippAn:
    '<svg viewBox="0 0 34 24" width="24" height="17" aria-hidden="true"><rect x="1" y="5" width="32" height="14" rx="7" fill="currentColor" fill-opacity="0.85"/><circle cx="26" cy="12" r="5" fill="var(--paper-elev)"/></svg>',
  kippAus:
    '<svg viewBox="0 0 34 24" width="24" height="17" aria-hidden="true"><rect x="1.9" y="5.9" width="30.2" height="12.2" rx="6.1" fill="none" stroke="currentColor" stroke-width="1.6" stroke-opacity="0.55"/><circle cx="8" cy="12" r="4.2" fill="currentColor" fill-opacity="0.55"/></svg>',
  zurueck:
    '<svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 4 12l7 7"/><path d="M20 12H4.6"/></svg>',
  vor: '<svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="m13 5 7 7-7 7"/><path d="M4 12h15.4"/></svg>',
} as const;

interface PultSchalter {
  /** Stabiler Schlüssel — daran findet `setzePult` den Knopf wieder. */
  rolle: string;
  /** Ein Wort. Zwei brechen um und sehen aus wie Fließtext. */
  name: string;
  /** Markup aus PULT_ZEICHEN. Ohne Zeichen steht nur das Wort da. */
  zeichen?: string;
  /** Umschalter: an oder aus. Fehlt die Angabe, ist es eine Handlung. */
  an?: boolean;
  gesperrt?: boolean;
  /** Was gesprochen wird, wenn das Wort allein nicht reicht. */
  ansage?: string;
  beiKlick?: () => void;
}

function fuelle(b: HTMLButtonElement, s: PultSchalter): void {
  b.dataset.rolle = s.rolle;
  b.disabled = s.gesperrt === true;
  if (s.an === undefined) b.removeAttribute("aria-pressed");
  else b.setAttribute("aria-pressed", s.an ? "true" : "false");
  b.dataset.an = s.an ? "ja" : "nein";
  const ansage = s.ansage ?? s.name;
  b.setAttribute("aria-label", ansage);
  b.title = ansage;
  const zeichen = b.querySelector<HTMLElement>(".pult-zeichen");
  const wort = b.querySelector<HTMLElement>(".pult-wort");
  if (zeichen) {
    zeichen.innerHTML = s.zeichen ?? "";
    zeichen.hidden = !s.zeichen;
  }
  if (wort) wort.textContent = s.name;
}

/** Ein Pult bauen. Die Reihenfolge der Schalter ist die im Bild. */
export function bauePult(schalter: readonly PultSchalter[]): HTMLElement {
  const pult = document.createElement("div");
  pult.className = "pult";
  pult.setAttribute("role", "group");
  for (const s of schalter) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "pult-schalter";
    const z = document.createElement("span");
    z.className = "pult-zeichen";
    z.setAttribute("aria-hidden", "true");
    const w = document.createElement("span");
    w.className = "pult-wort";
    b.append(z, w);
    fuelle(b, s);
    if (s.beiKlick) b.addEventListener("click", s.beiKlick);
    pult.append(b);
  }
  return pult;
}

/**
 * Ein bestehendes Pult neu beschriften — ohne es neu zu bauen.
 *
 * ⚠️ Neu bauen wäre einfacher und wäre falsch: Der Knopf unter dem Finger
 *    verlöre den Fokus, und ein Druck während des Neuzeichnens ginge ins
 *    Leere. Deshalb werden dieselben Elemente umbeschriftet.
 */
export function setzePult(
  pult: HTMLElement,
  schalter: readonly PultSchalter[],
): void {
  for (const s of schalter) {
    const b = pult.querySelector<HTMLButtonElement>(
      `.pult-schalter[data-rolle="${s.rolle}"]`,
    );
    if (b) fuelle(b, s);
  }
}
